// PASTE #229 Wave 3: wedding stem-to-box quantity math. PURE + public -
// quantities ONLY, never prices (the pricing gate lives elsewhere). Values
// derive from WAVE3_wedding-sourcing-guide.md:
//   - Per-design focal/rose stem counts (the guide's "Stem counts by
//     design" table) at Small/Medium/Large.
//   - Greenery: ~1 per 3 focal stems (guide rule of thumb).
//   - Hydrangea: counted by the head; documented per-design head counts
//     for centerpieces + installs.
//   - 10% breakage buffer on every category, then round UP to real pack
//     sizes: roses 25 stems/bunch, Quarter Box = 125 stems (5 bunches),
//     Half Box = 300; greenery 10 stems/bunch; hydrangea by single head.
// Cross-checks the guide's worked example: 20 medium low centerpieces =>
// 440 rose stems + buffer => 4 Quarter Boxes.

export type DesignKey =
  | "bridalBouquet"
  | "bridesmaidBouquet"
  | "boutonniere"
  | "corsage"
  | "lowCenterpiece"
  | "tallCenterpiece"
  | "archInstall";

export type SizeKey = "small" | "medium" | "large";

export const SIZE_INDEX: Record<SizeKey, 0 | 1 | 2> = {
  small: 0,
  medium: 1,
  large: 2,
};

// [S, M, L] focal/rose stems per design (guide table).
export const DESIGN_ROSE_STEMS: Record<DesignKey, [number, number, number]> = {
  bridalBouquet: [25, 32, 40],
  bridesmaidBouquet: [12, 15, 18],
  boutonniere: [1, 2, 3],
  corsage: [3, 4, 5],
  lowCenterpiece: [15, 22, 30],
  tallCenterpiece: [35, 42, 50],
  archInstall: [150, 275, 400],
};

// [S, M, L] hydrangea heads per design (0 where hydrangea is not typical).
export const DESIGN_HYDRANGEA_HEADS: Record<DesignKey, [number, number, number]> = {
  bridalBouquet: [0, 0, 0],
  bridesmaidBouquet: [0, 0, 0],
  boutonniere: [0, 0, 0],
  corsage: [0, 0, 0],
  lowCenterpiece: [2, 3, 4],
  tallCenterpiece: [3, 4, 5],
  archInstall: [20, 30, 45],
};

export const BUFFER = 1.1; // 10% breakage buffer (guide).
export const ROSE_STEMS_PER_BUNCH = 25;
export const GREENERY_STEMS_PER_BUNCH = 10;
export const QUARTER_BOX_STEMS = 125;
export const HALF_BOX_STEMS = 300;
export const GREENERY_PER_FOCAL = 1 / 3; // ~1 greenery per 3 focal.

export type CalcItem = { design: DesignKey; size: SizeKey; qty: number };

export type WeddingOrder = {
  roses: { stems: number; bunches: number; quarterBoxes: number; halfBoxes: number };
  greenery: { stems: number; bunches: number };
  hydrangea: { heads: number };
};

// Round a stem count up to full pack multiples.
function bunches(stems: number, per: number): number {
  return Math.ceil(stems / per);
}

// 10% breakage buffer, rounded up. Uses integer math (x11/10) rather
// than x1.1 so exact multiples don't drift up a whole unit from float
// error (400 * 1.1 === 440.00000000000006 -> would ceil to 441).
function withBuffer(stems: number): number {
  return Math.ceil((stems * 11) / 10);
}

export function computeWeddingOrder(items: CalcItem[]): WeddingOrder {
  let roseStems = 0;
  let hydrangeaHeads = 0;
  for (const it of items) {
    if (it.qty <= 0) continue;
    const i = SIZE_INDEX[it.size];
    roseStems += DESIGN_ROSE_STEMS[it.design][i] * it.qty;
    hydrangeaHeads += DESIGN_HYDRANGEA_HEADS[it.design][i] * it.qty;
  }
  const greeneryStems = Math.round(roseStems * GREENERY_PER_FOCAL);

  // 10% breakage buffer, rounded up, on every category.
  const rBuf = withBuffer(roseStems);
  const gBuf = withBuffer(greeneryStems);
  const hBuf = withBuffer(hydrangeaHeads);

  return {
    roses: {
      stems: rBuf,
      bunches: bunches(rBuf, ROSE_STEMS_PER_BUNCH),
      quarterBoxes: rBuf > 0 ? Math.ceil(rBuf / QUARTER_BOX_STEMS) : 0,
      halfBoxes: rBuf > 0 ? Math.ceil(rBuf / HALF_BOX_STEMS) : 0,
    },
    greenery: {
      stems: gBuf,
      bunches: bunches(gBuf, GREENERY_STEMS_PER_BUNCH),
    },
    hydrangea: { heads: hBuf },
  };
}
